<template>
  <v-container id="busineass_account_page_admin" fluid>
    <v-row class="mb-2 px-6" align="center">
      <v-col>
        <span class="text-h5">{{ $t('businessAccount.admin.title') }}</span>
      </v-col>
      <v-col cols="auto">
        <v-btn id="create_ba_button" color="primary" @click="openCreateDialog">
          <v-icon left>mdi-plus</v-icon>
          {{ $t('businessAccount.admin.create') }}
        </v-btn>
      </v-col>
    </v-row>

    <!-- Freemium Limits -->
    <v-row class="px-6 mb-4">
      <v-col cols="6">
        <v-card :loading="freemiumLoading" class="pa-3">
          <v-card-title class=" pb-4">
            {{ $t('businessAccount.admin.freemiumLimits.title') }}
          </v-card-title>
          <v-card-text class="pb-0">
            <v-row dense align="center">
              <v-col cols="auto" style="width: 160px">
                <v-text-field
                  id="freemium_monthly_limit"
                  v-model.number="freemiumLimits.monthlyLimit"
                  :label="$t('businessAccount.admin.freemiumLimits.monthlyLimit')"
                  type="number"
                  min="0"
                  outlined
                  dense
                  hide-details
                  :disabled="freemiumSaving"
                  :readonly="!freemiumEditing"
                ></v-text-field>
              </v-col>
              <v-col cols="auto" style="width: 160px">
                <v-text-field
                  id="freemium_yearly_limit"
                  v-model.number="freemiumLimits.yearlyLimit"
                  :label="$t('businessAccount.admin.freemiumLimits.yearlyLimit')"
                  type="number"
                  min="0"
                  outlined
                  dense
                  hide-details
                  :disabled="freemiumSaving"
                  :readonly="!freemiumEditing"
                ></v-text-field>
              </v-col>
              <v-spacer></v-spacer>
              <v-col cols="auto">
                <template v-if="freemiumEditing">
                  <v-btn id="freemium_cancel_button" class="mr-2" text small @click="cancelFreemiumEdit">
                    {{ $t('main.cancel') }}
                  </v-btn>
                  <v-btn
                    id="freemium_save_button"
                    color="primary"
                    small
                    :loading="freemiumSaving"
                    :disabled="freemiumSaving"
                    @click="saveFreemiumLimits"
                  >
                    {{ $t('main.save') }}
                  </v-btn>
                </template>
                <v-btn v-else id="freemium_edit_button" text small color="primary" @click="startFreemiumEdit">
                  <v-icon small left>mdi-pencil</v-icon>
                  {{ $t('businessAccount.admin.freemiumLimits.edit') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Search -->
    <v-row class="px-6 mb-2">
      <v-col cols="12" md="4">
        <v-text-field
          id="business_account_search_field"
          v-model="search"
          :label="$t('businessAccount.admin.search')"
          prepend-inner-icon="mdi-magnify"
          clearable
          dense
          outlined
          hide-details
          @keyup.enter="loadBusinessAccounts"
          @click:clear="onSearchClear"
        ></v-text-field>
      </v-col>
    </v-row>

    <!-- BA List Table -->
    <v-row class="px-6">
      <v-col>
        <v-data-table
          id="ba_admin_table"
          :headers="headers"
          :items="businessAccounts"
          :options.sync="pagination"
          :server-items-length="totalItems"
          :loading="loading"
          must-sort
          class="elevation-1"
          :no-data-text="$t('businessAccount.admin.noData')"
          :loading-text="$t('dataTable.content.loadingPleaseWait')"
          :footer-props="{
            'items-per-page-text': $t('dataTable.footer.itemsPerPageText'),
            'page-text': $t('dataTable.footer.pageText'),
            'items-per-page-options': [5, 10, 25, 50],
          }"
        >
          <template #item.createdAt="{ item }">
            {{ formatDate(item.createdAt) }}
          </template>
          <template #item.actions="{ item }">
            <v-btn
              id="manage_ba_button"
              small
              text
              color="primary"
              class="mr-1"
              @click="manageBA(item)"
            >
              <v-icon small left>mdi-cog</v-icon>
              {{ $t('businessAccount.admin.manage') }}
            </v-btn>
            <v-btn
              id="delete_ba_button"
              icon
              small
              color="error"
              :disabled="deleting"
              @click="deleteBA(item, $event)"
            >
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <!-- Create BA Dialog -->
    <v-dialog id="create_ba_modal_root" v-model="createDialog" max-width="700" persistent>
      <v-card>
        <v-card-title>
          <v-icon left>mdi-domain-plus</v-icon>
          {{ $t('businessAccount.admin.createTitle') }}
        </v-card-title>
        <v-card-text>
          <v-form ref="createForm" v-model="createFormValid">
            <v-text-field
              id="business_account_name"
              v-model="createReq.name"
              :label="$t('businessAccount.admin.name')"
              :rules="[v => !!v || $t('validation.form.requiredField')]"
              outlined
              dense
              class="mb-4"
            ></v-text-field>

            <!-- Providers -->
            <div class="subtitle-2 font-weight-bold mb-2">
              {{ $t('businessAccount.admin.providers') }}
            </div>

            <v-card
              v-for="(provider, idx) in createReq.providers"
              :key="idx"
              outlined
              class="mb-3 pa-3"
            >
              <!-- EXTERNAL: two rows -->
              <template v-if="provider.providerType === 'EXTERNAL'">
                <v-row dense align="center">
                  <v-col cols="12" sm="3">
                    <v-select
                      :id="'provider_type_' + idx"
                      v-model="provider.providerType"
                      :items="availableCreateProviderTypes(idx)"
                      :label="$t('businessAccount.admin.providerType')"
                      :rules="[v => !!v || $t('validation.form.requiredField')]"
                      outlined
                      dense
                      hide-details
                      @change="onProviderTypeChange(provider)"
                    ></v-select>
                  </v-col>
                  <v-col cols="12" sm="4">
                    <v-text-field
                      :id="'provider_kind_' + idx"
                      v-model="provider.providerKind"
                      :label="$t('businessAccount.admin.providerKind')"
                      outlined
                      dense
                      hide-details
                    ></v-text-field>
                  </v-col>
                  <v-col cols="auto" class="ml-auto">
                    <v-btn icon small color="error" @click="removeProvider(idx)">
                      <v-icon small>mdi-close</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>
                <v-row dense align="center" class="mt-2">
                  <v-col cols="12" sm="3">
                    <v-text-field
                      :id="'monthly_limit_' + idx"
                      v-model.number="provider.monthlyLimit"
                      :label="$t('businessAccount.admin.monthlyLimit')"
                      type="number"
                      min="0"
                      outlined
                      dense
                      hide-details
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="4">
                    <v-text-field
                      :id="'yearly_limit_' + idx"
                      v-model.number="provider.yearlyLimit"
                      :label="$t('businessAccount.admin.yearlyLimit')"
                      type="number"
                      min="0"
                      outlined
                      dense
                      hide-details
                    ></v-text-field>
                  </v-col>
                </v-row>
              </template>

              <!-- Non-EXTERNAL: single row -->
              <v-row v-else dense align="center">
                <v-col cols="12" sm="3">
                  <v-select
                    :id="'provider_type_' + idx"
                    v-model="provider.providerType"
                    :items="availableCreateProviderTypes(idx)"
                    :label="$t('businessAccount.admin.providerType')"
                    :rules="[v => !!v || $t('validation.form.requiredField')]"
                    outlined
                    dense
                    hide-details
                    @change="onProviderTypeChange(provider)"
                  ></v-select>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field
                    :id="'monthly_limit_' + idx"
                    v-model.number="provider.monthlyLimit"
                    :label="$t('businessAccount.admin.monthlyLimit')"
                    type="number"
                    min="0"
                    outlined
                    dense
                    hide-details
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field
                    :id="'yearly_limit_' + idx"
                    v-model.number="provider.yearlyLimit"
                    :label="$t('businessAccount.admin.yearlyLimit')"
                    type="number"
                    min="0"
                    outlined
                    dense
                    hide-details
                  ></v-text-field>
                </v-col>
                <v-col cols="auto">
                  <v-btn :id="'remove_provider_' + idx" icon small color="error" @click="removeProvider(idx)">
                    <v-icon small>mdi-close</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </v-card>

            <v-btn id="add_provider_button" text color="primary" small :disabled="createProviderTypesExhausted" @click="addProvider">
              <v-icon small left>mdi-plus</v-icon>
              {{ $t('businessAccount.admin.addProvider') }}
            </v-btn>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn id="close_modal_button" text @click="closeCreateDialog">{{ $t('main.cancel') }}</v-btn>
          <v-btn
            id="submit_create_ba_button"
            color="primary"
            :disabled="!createFormValid || creating"
            :loading="creating"
            @click="submitCreate"
          >
            {{ $t('main.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import businessAccount from '@/services/api/businessAccount'
import seal from '@/services/api/seal'
import credentialRefs from '@/services/api/credentialRefs'
import { showConfirmationMenu, showToastError, showToastSuccess } from '../../event-bus'

export default {
  name: 'BusinessAccountAdmin',

  data() {
    return {
      clientName: 'web',
      loading: false,
      deleting: false,
      creating: false,
      createDialog: false,
      createFormValid: false,
      search: '',
      businessAccounts: [],
      totalItems: 0,
      pagination: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ['name'],
        sortDesc: [false]
      },
      headers: [
        { text: this.$t('businessAccount.admin.headers.id'), value: 'id' },
        { text: this.$t('businessAccount.admin.headers.name'), value: 'name' },
        { text: this.$t('businessAccount.admin.headers.createdAt'), value: 'createdAt' },
        { text: this.$t('businessAccount.admin.headers.actions'), value: 'actions', sortable: false }
      ],
      // providerTypeOptions: ['INTERNAL', 'CENTRAL', 'EXTERNAL', 'EVRO', 'ALFA'],
      providers: [],
      evroEnabled: this.$store.state.remoteConfig.evroEnabled
        ? this.$store.state.remoteConfig.evroEnabled
        : false,
      sealStatus: {
        enabled: false,
        secureMode: false,
        activated: false
      },
      alfaEnabled: this.$store.state.remoteConfig.alfaEnabled ? this.$store.state.remoteConfig.alfaEnabled : false,
      createReq: {
        name: '',
        providers: []
      },
      freemiumLimits: {
        monthlyLimit: 0,
        yearlyLimit: 0
      },
      freemiumLimitsBackup: null,
      freemiumLoading: false,
      freemiumSaving: false,
      freemiumEditing: false
    }
  },

  computed: {
    sealActivated() {
      return this.sealStatus.enabled && this.sealStatus.activated
    },

    dynamicProviderTypeOptions() {
      const options = ['INTERNAL']
      if (this.sealActivated) options.push('CENTRAL')
      if (this.evroEnabled) options.push('EVRO')
      if (this.alfaEnabled) options.push('ALFA')
      if (this.externalProvidersEnabled) options.push('EXTERNAL')
      return options
    },

    createProviderTypesExhausted() {
      const usedNonExternal = this.createReq.providers
        .filter(p => p.providerType && p.providerType !== 'EXTERNAL')
        .map(p => p.providerType)
      const availableNonExternal = this.dynamicProviderTypeOptions.filter(t => t !== 'EXTERNAL')
      const allNonExternalUsed = availableNonExternal.every(t => usedNonExternal.includes(t))
      if (this.externalProvidersEnabled) return false
      return allNonExternalUsed
    },

    externalProvidersEnabled() {
      return this.providers?.length > 0
    }
  },

  watch: {
    pagination: {
      handler() {
        if (!this.loading) {
          this.loadBusinessAccounts()
        }
      }
    }
  },

  async mounted() {
    this.sealStatus = await seal.getSealStatus()
    // console.log('this.sealStatus', this.sealStatus)
    this.providers = await credentialRefs.getExternalProviders(this.clientName)
    this.loadBusinessAccounts()
    this.loadFreemiumLimits()
  },

  methods: {
    getErrorMessage(error) {
      return error?.response?.data?.message || error?.message || this.$t('toastMessage.errorWhileRetrievingData')
    },

    async loadBusinessAccounts() {
      this.loading = true
      try {
        const pageRequest = {
          'page-size': this.pagination.itemsPerPage,
          'page-num': this.pagination.page,
          'sort-field': this.pagination.sortBy[0] || 'name',
          'sort-order': this.pagination.sortDesc[0] ? 'DESC' : 'ASC'
        }
        if (this.search) {
          pageRequest.q = this.search
        }
        const resp = await businessAccount.listBusinessAccounts(pageRequest)
        this.businessAccounts = resp.content || []
        this.totalItems = resp.total || 0
      } catch (error) {
        showToastError(this.getErrorMessage(error))
      } finally {
        this.loading = false
      }
    },

    onSearchClear() {
      this.search = ''
      this.loadBusinessAccounts()
    },

    manageBA(item) {
      this.$router.push({ name: 'BusinessAccountManage', params: { baId: item.id } })
    },

    async deleteBA(item, e) {
      const opts = { title: this.$t('dialog.confirmation.title'), posX: e.clientX, posY: e.clientY }
      const conf = await showConfirmationMenu(opts)
      if (!conf) return
      this.deleting = true
      try {
        await businessAccount.deleteBusinessAccount(item.id)
        showToastSuccess(this.$t('businessAccount.admin.deleteSuccess'))
        await this.loadBusinessAccounts()
      } catch (error) {
        showToastError(this.getErrorMessage(error))
      } finally {
        this.deleting = false
      }
    },

    openCreateDialog() {
      this.createReq = { name: '', providers: [] }
      this.createDialog = true
      this.$nextTick(() => {
        if (this.$refs.createForm) this.$refs.createForm.resetValidation()
      })
    },

    closeCreateDialog() {
      this.createDialog = false
    },

    addProvider() {
      this.createReq.providers.push({
        providerType: '',
        providerKind: '',
        monthlyLimit: 0,
        yearlyLimit: 0
      })
    },

    removeProvider(idx) {
      this.createReq.providers.splice(idx, 1)
    },

    availableCreateProviderTypes(currentIdx) {
      const usedNonExternal = this.createReq.providers
        .filter((p, i) => i !== currentIdx && p.providerType && p.providerType !== 'EXTERNAL')
        .map(p => p.providerType)
      return this.dynamicProviderTypeOptions.map(t => ({
        text: t,
        value: t,
        disabled: t !== 'EXTERNAL' && usedNonExternal.includes(t)
      }))
    },

    onProviderTypeChange(provider) {
      if (provider.providerType !== 'EXTERNAL') {
        provider.providerKind = undefined
      } else {
        provider.providerKind = ''
      }
    },

    async submitCreate() {
      if (!this.$refs.createForm.validate()) return
      this.creating = true
      try {
        const providers = this.createReq.providers.map(p => {
          const obj = {
            providerType: p.providerType,
            monthlyLimit: p.monthlyLimit,
            yearlyLimit: p.yearlyLimit
          }
          if (p.providerType === 'EXTERNAL' && p.providerKind) {
            obj.providerKind = p.providerKind
          }
          return obj
        })
        await businessAccount.createBusinessAccount({
          name: this.createReq.name,
          providers
        })
        showToastSuccess(this.$t('businessAccount.admin.createSuccess'))
        this.closeCreateDialog()
        await this.loadBusinessAccounts()
      } catch (error) {
        showToastError(this.getErrorMessage(error))
      } finally {
        this.creating = false
      }
    },

    async loadFreemiumLimits() {
      this.freemiumLoading = true
      try {
        const data = await businessAccount.getDefaultProviderLimits()
        this.freemiumLimits = {
          monthlyLimit: data.monthlyLimit,
          yearlyLimit: data.yearlyLimit
        }
      } catch (error) {
        showToastError(this.getErrorMessage(error))
      } finally {
        this.freemiumLoading = false
      }
    },

    startFreemiumEdit() {
      this.freemiumLimitsBackup = { ...this.freemiumLimits }
      this.freemiumEditing = true
    },

    cancelFreemiumEdit() {
      this.freemiumLimits = { ...this.freemiumLimitsBackup }
      this.freemiumLimitsBackup = null
      this.freemiumEditing = false
    },

    async saveFreemiumLimits() {
      this.freemiumSaving = true
      try {
        await businessAccount.updateDefaultProviderLimits({
          monthlyLimit: this.freemiumLimits.monthlyLimit,
          yearlyLimit: this.freemiumLimits.yearlyLimit
        })
        showToastSuccess(this.$t('businessAccount.admin.freemiumLimits.saveSuccess'))
        this.freemiumLimitsBackup = null
        this.freemiumEditing = false
      } catch (error) {
        showToastError(this.getErrorMessage(error))
      } finally {
        this.freemiumSaving = false
      }
    },

    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')
      return `${year}.${month}.${day}, ${hours}:${minutes}:${seconds}`
    }
  }
}
</script>
